import { redirect } from "react-router";
import type { ActionFunctionArgs, Params } from "react-router";
import { useSuspenseQueries } from "@tanstack/react-query";
import type { Me, Space, Tenant } from "@repo/shared-types";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { errorMessage, formError } from "@/lib/session";
import { requireSession } from "@/lib/guards";
import { holds } from "@/lib/grants";
import { PERSONAL_PATH, tenantPath } from "@/lib/tenants";
import { looksLikeAPicture, readCrop, uploadPicture } from "@/lib/images";
import { readInvitee } from "@/lib/invitees";
import { toast } from "@/lib/toast";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";
import { ErrorNote } from "@/components/form";

/**
 * What a space's own pages share — Members, Requests, Invite and Settings,
 * each one link from the space's menu: who is asking, what they may do there,
 * one action for every form on them, and the header each starts with.
 *
 * What a page offers is decided by the caller's role, which arrives on the
 * session, and by their grants, which arrive on the space's own read (0084).
 * That is presentation only — the api refuses anything they do not
 * allow, whatever the page renders. A page they may not use sends them back
 * to the space, rather than showing an error for a link they followed.
 */

export type Section = {
  tenant: Tenant;
  role: string;
  grants: string[];
  me: string;
  isOwner: boolean;
  /** The owner's, and an admin's who holds manage_members; never a personal space's. */
  canManageMembers: boolean;
  /** Whether pictures are switched on here (7g), so a space's may be set. */
  images: boolean;
};

/**
 * The space and the caller's place in it, for the page named `page`: warms
 * the reads `useSection` makes, and hands the loader what it decides by.
 */
export async function loadSection(request: Request, params: Params, page: string): Promise<Section> {
  const session = await requireSession(request);
  const slug = params.slug ?? "";

  // A personal space has one address people see, /t/me. Reached by its
  // real slug — its id — it goes there instead, before asking the api anything.
  const personal = session.memberships.find((m) => m.tenant.isPersonal)?.tenant;
  if (personal && slug === personal.slug) throw redirect(`${PERSONAL_PATH}/${page}`);

  // "me" is passed through as it is: the api resolves it to the caller's own.
  // A non-member gets 404 from both, which is the same answer a missing space
  // gives. The router's error boundary renders it.
  return sectionOf(session, await read(queries.space(slug)));
}

/**
 * The same, for the page's component: read from the queries `loadSection`
 * warmed, so a fresh answer redraws the page.
 */
export function useSection(slug: string): Section {
  const [{ data: session }, { data: space }] = useSuspenseQueries({
    queries: [queries.me(), queries.space(slug)],
  });
  // The loader's guard let the page draw only with a session.
  if (!session) throw new Error("useSection needs a session");
  return sectionOf(session, space);
}

function sectionOf(session: Me, space: Space): Section {
  // By id, not by the path segment: at /t/me the segment names no slug.
  const role = session.memberships.find((m) => m.tenant.id === space.id)?.role ?? "member";
  const { grants, memberCount: _count, requestCount: _requests, ...tenant } = space;

  return {
    tenant,
    role,
    grants,
    me: session.user.id,
    isOwner: role === "owner",
    canManageMembers: !tenant.isPersonal && holds(role, grants, "manage_members"),
    images: session.images,
  };
}

/** Back to the space's feed, for whoever reached a page that is not theirs. */
export function backToSpace(section: Section): Response {
  return redirect(tenantPath(section.tenant));
}

export type ActionResult = {
  error?: string;
  /** What worked, as its toast said it: a dialog's fetcher closes on it. */
  ok?: string;
  /** The box an error is about, shown beside it. */
  field?: string;
};

/**
 * Says what worked, in the toast above the tab bar (decided with Tiago 12
 * October): most saves here change something not drawn where the person is —
 * a role inside a closed sheet, who can find the space — and the rest say it
 * the same way, so every save on these pages reads alike. An error is never
 * a toast: it stays beside the box it is about.
 */
function saved(message: string): ActionResult {
  toast(message);
  return { ok: message };
}

/**
 * Every form on a space's own pages, told apart by its intent: each page
 * sends only its own, and they share the answers.
 */
export async function spaceSectionAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const form = await request.formData();
  const intent = String(form.get("intent") ?? "");
  const space = api.space(params.slug ?? "");

  try {
    switch (intent) {
      case "invite": {
        // One box, a username or an address (decided with Tiago 12
        // October), told apart here so the api is sent exactly one.
        const invitee = readInvitee(String(form.get("invitee") ?? ""));
        if (!invitee.email && !invitee.username) {
          return { error: "Type their username or email address.", field: "invitee" };
        }
        await space.invitations.create({ ...invitee, role: String(form.get("role") ?? "member") });
        return saved("Invitation sent.");
      }

      case "revoke":
        await space.invitations.revoke({
          email: String(form.get("email") ?? "") || undefined,
          username: String(form.get("username") ?? "") || undefined,
        });
        return saved("Invitation withdrawn.");

      case "set-role": {
        const role = String(form.get("role") ?? "member");
        await space.members.update(String(form.get("user") ?? ""), { role });
        return saved(role === "owner" ? "Handed on. You're an admin here now." : "Role updated.");
      }

      case "remove":
        await space.members.remove(String(form.get("user") ?? ""));
        return saved("Removed.");

      case "approve":
        await space.requests.approve(String(form.get("request") ?? ""), {
          role: String(form.get("role") ?? "member"),
        });
        return saved("Approved.");

      case "decline-request":
        await space.requests.decline(String(form.get("request") ?? ""));
        return saved("Declined.");

      case "rename":
        await space.update({ name: String(form.get("name") ?? "") });
        return saved("Renamed.");

      case "visibility": {
        const visibility = String(form.get("visibility") ?? "private");
        await space.update({ visibility });
        return saved(visibility === "public" ? "Now public." : "Now private.");
      }

      // The space's picture (0034): framed here, shrunk, posted to the store
      // and confirmed. Every list of spaces reads it from the session, which
      // is stale after either.
      case "picture": {
        const file = form.get("file");
        if (!looksLikeAPicture(file)) return { error: "Choose a picture." };
        try {
          await uploadPicture({
            start: () => space.image.start(),
            confirm: (mediaId) => space.media.uploaded(mediaId),
            file,
            crop: readCrop(form.get("crop")),
          });
        } catch (error) {
          return { error: errorMessage(error, "Could not save the picture") };
        }
        return saved("Picture saved.");
      }

      case "remove-picture":
        await space.image.remove();
        return saved("Picture removed. Its initials show instead.");

      case "grants":
        // Every grant the admin is to hold, each a repeated field — and none
        // is an empty list, which takes them all away. Each chip on the
        // member's sheet sends the whole set.
        await space.members.grants(String(form.get("user") ?? ""), {
          grants: form.getAll("grants").map(String),
        });
        return saved("Permissions updated.");

      case "delete": {
        // How many other people the page said it takes travels beside the
        // tick, and the api refuses it if more are in it now. Typing the name
        // is the confirmation — the address is an id, which nobody should
        // have to type — and the api checks that too, so both hold however
        // the form is submitted.
        const otherMembers = Number(form.get("otherMembers") ?? 0);
        if (otherMembers > 0 && form.get("forThem") === null) {
          return {
            error: `Tick the box to delete it for the ${counted(otherMembers, "other member", "other members")} too.`,
            field: "otherMembers",
          };
        }
        try {
          await space.delete({ confirm: String(form.get("confirm") ?? ""), otherMembers });
        } catch (error) {
          return formError(error, "Could not delete the space");
        }
        // Said by name: the spaces page shows it only by its absence.
        toast(`${String(form.get("name") ?? "") || "The space"} is deleted.`);
        return redirect("/spaces");
      }

      default:
        return { error: "Unknown action" };
    }
  } catch (error) {
    return { error: errorMessage(error, "Something went wrong") };
  }
}

/** A space's own page starts with the way back to it, and what this page is. */
export function SectionHeader({ tenant, title }: { tenant: Tenant; title: string }) {
  return (
    <div className="space-y-2">
      <PageTitle of={[title, tenant.name]} />
      <BackLink to={tenantPath(tenant)}>{tenant.name}</BackLink>
      <h1 className="page-title">{title}</h1>
    </div>
  );
}

/**
 * What went wrong with the last form sent, when it names no box of its own.
 * What went right is the action's toast, so nothing is drawn for it here.
 */
export function ActionNotes({ result }: { result: ActionResult | undefined }) {
  return result?.error && !result.field ? <ErrorNote message={result.error} /> : null;
}

/** A number and its noun, one or many: "1 event", "1,204 comments". */
export function counted(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}
